import { Form, Head, router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import Heading from "@/components/heading"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AppLayout from "@/layouts/app-layout"
import { editServerPath, serverPath, serversPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { Server } from "@/types"

interface ServerEditProps {
  server: Server
}

export default function ServerEdit({ server }: ServerEditProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
    { title: "Edit", href: editServerPath(server.id) },
  ]

  function handleDelete() {
    if (
      confirm(
        `Are you sure you want to delete "${server.name}"? This will also delete all apps and backups.`,
      )
    ) {
      router.delete(serverPath(server.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${server.name}`} />

      <div className="p-6">
        <Heading
          title={`Edit ${server.name}`}
          description="Update server configuration"
        />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>Server Details</CardTitle>
            <CardDescription>
              Update the basic information about your server
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form
              method="put"
              action={serverPath(server.id)}
              className="space-y-6"
            >
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="server[name]"
                      type="text"
                      required
                      defaultValue={server.name}
                    />
                    <InputError messages={errors["server.name"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="hostname">Hostname</Label>
                    <Input
                      id="hostname"
                      name="server[hostname]"
                      type="text"
                      defaultValue={server.hostname || ""}
                      placeholder="server.example.com"
                    />
                    <InputError messages={errors["server.hostname"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="info">Info (Markdown)</Label>
                    <textarea
                      id="info"
                      name="server[info]"
                      rows={5}
                      defaultValue={server.info || ""}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Server notes and documentation..."
                    />
                    <InputError messages={errors["server.info"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="watched_directories">
                      Watched Directories
                    </Label>
                    <textarea
                      id="watched_directories"
                      name="server[settings][watched_directories]"
                      rows={3}
                      defaultValue={
                        Array.isArray(server.settings?.watched_directories)
                          ? server.settings.watched_directories.join("\n")
                          : ""
                      }
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="/home/deploy/backups&#10;/var/log"
                    />
                    <p className="text-muted-foreground text-xs">
                      One directory path per line. These directories will be
                      tracked separately in disk usage reports.
                    </p>
                    <InputError
                      messages={errors["server.settings.watched_directories"]}
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Server
                    </Button>
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => history.back()}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={processing}>
                        {processing && <Spinner />}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
